import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrown,
  faMedal,
  faAward,
  faChevronRight,
  faUsers,
  faCalendar,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";
import LeagueStandings from "./LeagueStandings";

function getRankIcon(rank) {
  switch (rank) {
    case 1:
      return (
        <FontAwesomeIcon icon={faCrown} className="w-4 h-4 text-amber-400" />
      );
    case 2:
      return (
        <FontAwesomeIcon icon={faMedal} className="w-4 h-4 text-gray-400" />
      );
    case 3:
      return (
        <FontAwesomeIcon icon={faAward} className="w-4 h-4 text-amber-600" />
      );
    default:
      return (
        <span className="text-xs font-bold text-medium-grey">#{rank}</span>
      );
  }
}

const LeagueCard = ({
  league,
  userId,
  isExpanded,
  onToggle,
  onViewDetails,
  onViewSubmissions,
}) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Fetch members to get user rank and total count
  const { data: membersData } = useLeagueMembers(league._id, 1, 100, "");

  const members = membersData?.data || [];
  const totalMemberCount = membersData?.total || league.members?.length || 0;

  // Find user's rank from sorted members
  const userIndex = members.findIndex((m) => m.userId === userId);
  const userRank = userIndex !== -1 ? userIndex + 1 : null;
  const isFirstPlace = userRank === 1;

  // Measure content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, membersData]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-card">
      {/* League Header */}
      <button
        onClick={() => onToggle(league._id)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
              isFirstPlace
                ? "bg-gradient-to-br from-amber-400 to-amber-600 text-amber-950"
                : "bg-gray-100 text-dark-grey"
            }`}
          >
            {league.leagueName?.charAt(0) || "L"}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-dark-grey">
              {league.leagueName}
            </h3>
            <div className="flex items-center gap-2 text-xs text-medium-grey">
              <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
                {totalMemberCount}/
                {league.numPlayers || league.maxMembers || "∞"}
              </span>
              {league.currentEvent && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <FontAwesomeIcon
                      icon={faCalendar}
                      className="text-[10px]"
                    />
                    {league.currentEvent}
                  </span>
                </>
              )}
              {league.isLive && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-green-500">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                    </span>
                    Live
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            {userRank && (
              <div className="flex items-center gap-1 justify-end">
                {getRankIcon(userRank)}
              </div>
            )}
          </div>
          <FontAwesomeIcon
            icon={faChevronRight}
            className={`text-medium-grey text-sm transition-transform duration-300 ease-in-out ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
        </div>
      </button>

      {/* Standings - Smooth expand/collapse */}
      <div
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : "0px",
          opacity: isExpanded ? 1 : 0,
          transition:
            "max-height 350ms cubic-bezier(0.4, 0, 0.2, 1), opacity 250ms ease-in-out",
        }}
        className="overflow-hidden"
      >
        <div ref={contentRef} className="border-t border-gray-200">
          <div className="p-3">
            <LeagueStandings preview={true} leagueId={league._id} hideHeader />
          </div>

          {/* Action buttons */}
          <div className="p-3 pt-0 flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onViewSubmissions) onViewSubmissions();
              }}
              className="flex-1 py-2 px-3 border border-gray-200 rounded-lg text-xs text-dark-grey hover:bg-gray-50 transition-colors font-medium"
            >
              View Submissions
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onViewDetails) onViewDetails();
              }}
              className="flex-1 py-2 px-3 bg-deep-red text-white rounded-lg text-xs hover:bg-bright-red transition-colors font-medium"
            >
              League Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyLeagues = ({
  leagues = [],
  userId,
  onJoinLeague,
  onViewLeagueDetails,
  onViewSubmissions,
  onLeagueSelect,
}) => {
  const navigate = useNavigate();
  const [expandedLeagueId, setExpandedLeagueId] = useState(null);

  const handleToggle = (leagueId) => {
    const newId = expandedLeagueId === leagueId ? null : leagueId;
    setExpandedLeagueId(newId);
    if (onLeagueSelect) {
      onLeagueSelect(newId);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-dark-grey">My Leagues</h2>
        <button
          onClick={onJoinLeague || (() => navigate("/create-league"))}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-dark-grey hover:bg-gray-50 transition-colors flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
          Join League
        </button>
      </div>

      <div className="space-y-3">
        {leagues.map((league) => (
          <LeagueCard
            key={league._id}
            league={league}
            userId={userId}
            isExpanded={expandedLeagueId === league._id}
            onToggle={handleToggle}
            onViewDetails={() => {
              if (onViewLeagueDetails) onViewLeagueDetails(league._id);
            }}
            onViewSubmissions={() => {
              if (onViewSubmissions) onViewSubmissions(league._id);
            }}
          />
        ))}

        {leagues.length === 0 && (
          <div className="text-center py-8 bg-white rounded-2xl border border-gray-200">
            <FontAwesomeIcon
              icon={faUsers}
              className="text-3xl text-gray-300 mb-3"
            />
            <p className="text-sm text-medium-grey mb-3">No leagues yet</p>
            <button
              onClick={onJoinLeague || (() => navigate("/create-league"))}
              className="px-4 py-2 bg-deep-red text-white text-sm font-bold rounded-lg hover:bg-bright-red transition-colors"
            >
              Create or Join a League
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLeagues;
